// İçeri alma önizlemesi durumu (görünümden bağımsız). Kurallar:
// - Ekrandaki görünüm ve seçimler her zaman aynı (son başarılı) isteğe aittir; başarısız deneme yalnız "Yeniden dene" için saklanır.
// - Önizleme beklenirken karar girdisi yok sayılır (loadingRef).
// - Tek depo yalnız sunucu listesi tam ve tek seçenekliyken otomatik seçilir; sunucu her önizlemede yeniden doğrular.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { EMPTY_CHOICE, collectIssues, payload } from "./model";

export function useQuickImportPreview({ open, invoice, onReset, onRetried }) {
  const cb = useRef({});
  cb.current = { onReset, onRetried };
  const [mode, setMode] = useState("expense_only");
  const [lineChoices, setLineChoices] = useState([]);
  const [createSupplier, setCreateSupplier] = useState(false);
  const [supplierId, setSupplierId] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [autoSelectedId, setAutoSelectedId] = useState(null);
  const [previewError, setPreviewError] = useState(null);     // null | "rate" | "server" | "view": son DENEME başarısız
  // Ekrandaki görünüm ile seçimler HER ZAMAN aynı (son başarılı) isteğe aittir. Başarısız deneme yalnız "Yeniden dene" için saklanır.
  const attemptRef = useRef(null);                            // en son denenen istek {args, auto, touched}
  const committedRef = useRef(null);                          // son BAŞARILI isteğin seçimleri
  const decisionRef = useRef(null);                           // son BAŞARILI karar
  const autoRef = useRef(null);
  const warehouseTouched = useRef(false);               // kullanıcı depo kararı verdiyse otomatik seçim bir daha yapılmaz (stok kapatılana dek)
  const requestSequence = useRef(0);
  const [announce, setAnnounce] = useState("");
  const loadingRef = useRef(false);
  const retrying = useRef(false);

  const documentId = String(invoice?.id || "").trim();

  const load = useCallback(async (nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse) => {
    if (!documentId) return;
    const sequence = ++requestSequence.current;
    const attempt = { args: [nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse], auto: autoRef.current, touched: warehouseTouched.current };
    attemptRef.current = attempt;
    setLoading(true); loadingRef.current = true;
    if (decisionRef.current) setAnnounce("Önizleme güncelleniyor.");
    const apply = (snap) => {
      const [m, c, cs, sid, wid] = snap.args;
      setMode(m); setLineChoices(c); setCreateSupplier(cs); setSupplierId(sid); setWarehouseId(wid);
      autoRef.current = snap.auto; setAutoSelectedId(snap.auto); warehouseTouched.current = snap.touched;
    };
    // Başarısız deneme: seçimler son başarılı isteğe geri döner (görünümle eşleşir); deneme uygulanmamış sayılır ve hazır denmez.
    const keepPrevious = (kind) => {
      if (decisionRef.current?.document_view && committedRef.current) {
        apply(committedRef.current); setPreviewError(kind);
        setAnnounce("");                                   // uyarı (role=alert) tek başına duyurur
        return;
      }
      setPreviewError(null);
      setFailed(true);
      setDecision(null);
    };
    try {
      const result = await api.post(`/incoming-invoices/${encodeURIComponent(documentId)}/quick-import/preview`, payload(nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse));
      if (requestSequence.current !== sequence) return;
      if (result?.document_view_error || !result?.document_view) keepPrevious("view");
      else {
        if (decisionRef.current) setAnnounce("Önizleme güncellendi.");
        if (retrying.current) { retrying.current = false; setTimeout(() => cb.current.onRetried?.(), 0); } committedRef.current = attempt; apply(attempt); decisionRef.current = result; setDecision(result); setFailed(false); setPreviewError(null); }
    } catch (error) {
      if (requestSequence.current === sequence) keepPrevious(error?.status === 429 ? "rate" : "server");
    } finally {
      if (requestSequence.current === sequence) { setLoading(false); loadingRef.current = false; }
    }
  }, [documentId]);

  useEffect(() => {
    if (!open || !documentId) {
      requestSequence.current += 1;
      return undefined;
    }
    cb.current.onReset?.(); setMode("expense_only"); setLineChoices([]); setCreateSupplier(false); setSupplierId(null); setWarehouseId(null); setDecision(null); setFailed(false);
    setAutoSelectedId(null); warehouseTouched.current = false; setPreviewError(null); setAnnounce(""); attemptRef.current = null; committedRef.current = null; decisionRef.current = null; autoRef.current = null;
    load("expense_only", [], false, null, null);
    return () => { requestSequence.current += 1; };
  }, [open, documentId, load]);

  const view = decision?.document_view || null;
  const issues = useMemo(() => collectIssues(decision, view), [decision, view]);

  const reload = (patch) => {
    const next = { mode, lineChoices, createSupplier, supplierId, warehouseId, ...patch };
    load(next.mode, next.lineChoices, next.createSupplier, next.supplierId, next.mode === "expense_and_stock" ? next.warehouseId : null);
  };
  const withChoice = (index, patch) => {
    const next = [...lineChoices];
    while (next.length <= index) next.push({ ...EMPTY_CHOICE });
    next[index] = { ...next[index], ...patch };
    return next;
  };
  const chooseKind = (index, kind) => {
    if (loadingRef.current) return;
    // Hizmet satırında ürün kimliği, ürün taslağı ve stok çelişkilidir (sunucu reddeder): türle birlikte temizlenir.
    const next = withChoice(index, kind === "service" ? { kind, product_id: null, new_product_draft: false, stock: null } : { kind, stock: mode === "expense_and_stock" ? true : null });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseStock = (on) => {
    if (loadingRef.current) return;
    const productIndexes = (view?.lines || []).filter((l) => l.kind === "product").map((l) => l.index);
    let next = [...lineChoices];
    for (const index of productIndexes) {
      while (next.length <= index) next.push({ ...EMPTY_CHOICE });
      next[index] = { ...next[index], kind: "product", stock: on ? true : null };
    }
    if (!on) next = next.map((c) => ({ ...c, stock: c.stock === true ? null : c.stock }));
    const nextMode = on ? "expense_and_stock" : "expense_only";
    setMode(nextMode);
    setLineChoices(next);
    if (!on) { setWarehouseId(null); autoRef.current = null; setAutoSelectedId(null); warehouseTouched.current = false; }   // stok kapanınca depo kararı bırakılır
    reload({ mode: nextMode, lineChoices: next, warehouseId: on ? warehouseId : null });
  };
  const chooseProduct = (index, id) => {
    if (loadingRef.current) return;
    const next = withChoice(index, { kind: "product", product_id: id, new_product_draft: false });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseRemember = (index, on) => {
    if (loadingRef.current) return;
    const next = withChoice(index, { kind: "product", product_id: null, new_product_draft: on });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseSupplier = (id) => { if (loadingRef.current) return; setSupplierId(id); setCreateSupplier(false); reload({ supplierId: id, createSupplier: false }); };
  const chooseCreateSupplier = (on) => { if (loadingRef.current) return; setCreateSupplier(on); setSupplierId(null); reload({ createSupplier: on, supplierId: null }); };
  const chooseWarehouse = (id) => { if (loadingRef.current) return; warehouseTouched.current = true; autoRef.current = null; setAutoSelectedId(null); setWarehouseId(id); reload({ warehouseId: id }); };

  // Tek depo otomatik seçimi: stok açık, sunucu listesi BAŞARILI ve TAM, tek aktif yerel depo (liste dışı değil), henüz depo yok, kullanıcı karar vermemiş.
  // Aynı kararlarla önizleme yenilenir; depo sunucuda yeniden doğrulanır (pasifleşmişse WAREHOUSE_INVALID döner, döngü olmaz: depo artık seçili).
  useEffect(() => {
    const wh = decision?.document_view?.decisions?.warehouse;
    if (mode !== "expense_and_stock" || warehouseId || warehouseTouched.current || !wh) return;
    const options = wh.options || [];
    if (wh.required !== true || wh.selection_available !== true || wh.options_complete !== true || wh.selected_id) return;
    if (options.length !== 1 || options[0].outside_list || !options[0].id) return;
    setWarehouseId(options[0].id);
    autoRef.current = options[0].id;
    setAutoSelectedId(options[0].id);
    reload({ warehouseId: options[0].id });
  }, [decision]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = () => {
    const a = attemptRef.current;
    if (!a) return;
    autoRef.current = a.auto; warehouseTouched.current = a.touched;
    retrying.current = true;
    load(...a.args);
  };
  const stockOn = mode === "expense_and_stock";
  return { mode, lineChoices, createSupplier, supplierId, warehouseId, decision, loading, failed, autoSelectedId, previewError, announce,
    view, issues, stockOn, stale: previewError !== null, reload, retry,
    chooseKind, chooseStock, chooseProduct, chooseRemember, chooseSupplier, chooseCreateSupplier, chooseWarehouse };
}
